import React from 'react';
import { Avatar } from '@material-ui/core';
import { getColor } from 'common/constants/_colors';
import _ from 'lodash';
import { Flex } from 'components/base/layout-components';
import Text, { Inline } from 'components/base/Text';

import Table from 'components/base/Table';

import { getContributorAvatarUrl } from 'common/constants/Avatars';
import Checkbox from 'components/base/Checkbox';
import Tooltip from '@material-ui/core/Tooltip';

const prepareData =
  ({
    onCheckboxClicked,
    selectedContributorIds,
    hasMaxContributors,
    maxContributors,
    readonly
  }) =>
  contributors => {
    return _.orderBy(
      contributors,
      [
        ({ id }) => selectedContributorIds.includes(id),
        ({ name }) => name?.toLowerCase()
      ],
      ['desc', 'asc']
    ).map(contributor => {
      const {
        id,
        name = '',
        email,
        extended_data: { username = '' }
      } = contributor;
      const maxContributorsTooltip = (
        <Flex pt='small' pb='small' width='18rem' direction='column'>
          <Text color='shark' size='medium' weight='500' pb='1rem'>
            Want to add more members?
          </Text>
          <Text color='shuttleGray' size='medium' pb='0.25rem'>
            You can select {maxContributors} members max.
          </Text>
          <Text color='shuttleGray' size='medium' pb='0.25rem'>
            If you would like to add more,
          </Text>
          <Text color='shuttleGray' size='medium' pb='0.25rem'>
            upgrade your account.
          </Text>
        </Flex>
      );
      const nameCol = (
        <Flex items='center'>
          <Avatar src={getContributorAvatarUrl(contributor)} alt='' />
          <Flex direction='column'>
            <Inline size='large' pl='0.5rem' weight={400} ellipsisAfter='40rem'>
              {name ? name : username}
            </Inline>
            {email && (
              <Inline
                size='medium'
                pl='0.5rem'
                weight={400}
                ellipsisAfter='40rem'
                color='gray'
              >
                {email}
              </Inline>
            )}
          </Flex>
        </Flex>
      );
      const isChecked = selectedContributorIds.includes(id);
      const isDisabledContributor = !isChecked && hasMaxContributors;
      const checkBoxCol = (
        <Tooltip
          title={maxContributorsTooltip}
          disableHoverListener={!isDisabledContributor}
        >
          <span>
            <Checkbox
              data-testid={`contributorCheckbox-${id}`}
              disabled={isDisabledContributor || readonly}
              checked={isChecked}
              onChange={event => onCheckboxClicked(id, event.target.checked)}
              value={id}
            />
          </span>
        </Tooltip>
      );

      return {
        styles: isDisabledContributor
          ? { backgroundColor: getColor('chablis') }
          : {},
        name: nameCol,
        checkbox: checkBoxCol
      };
    });
  };

const getColumns = () => [
  { name: '', dataPoint: 'checkbox', isSortedCol: false },
  {
    name: 'Contributor name',
    dataPoint: 'name',
    isSortedCol: false,
    tableCellProps: { style: { width: '100%' } }
  }
];

const ContributorsTable = ({
  contributors = [],
  state: { maxContributors },
  selectedContributorIds = [],
  onCheckboxClicked,
  readonly
}) => {
  const contributorsKeys = _.keyBy(contributors, 'id');
  const selectedContributors = selectedContributorIds.map(
    contributorId => contributorsKeys[contributorId]
  );

  const contributorsLength = selectedContributorIds?.length || 0;
  const hasMaxContributors =
    !!maxContributors && contributorsLength === maxContributors;

  const tableContributors = readonly ? selectedContributors : contributors;

  return (
    <Flex
      direction='column'
      pt='small'
      grow={1}
      width='100%'
      style={{ overflow: 'hidden' }}
    >
      <Table
        withPagination={true}
        clientSidePagination={true}
        tableComponentProps={{
          style: {
            overflow: 'hidden',
            flexGrow: 1,
            maxHeight: '100%',
            display: 'flex'
          }
        }}
        data={tableContributors}
        columns={getColumns()}
        bodyScrollOptions={{
          enabled: true,
          bodyStyles: {
            overflowY: 'auto',
            overflowX: 'hidden',
            height: '100%',
            width: '100%',
            display: 'block',
            '& tr': {
              th: {
                width: '90%'
              }
            }
          }
        }}
        withTableHead={false}
        localSearchOptions={{
          enabled: true,
          placeholder: 'Search by contributor name',
          filterFn: (data = [], term = '') =>
            data.filter(
              ({ name = '', email = '' }) =>
                name?.toLowerCase().includes(term.toLowerCase()) ||
                email?.toLowerCase().includes(term.toLowerCase())
            ),
          prepareData: prepareData({
            onCheckboxClicked,
            selectedContributorIds,
            hasMaxContributors,
            maxContributors,
            readonly
          }),
          borderProps: { b: `1px solid ${getColor('black', 0.23)}` }
        }}
      />
    </Flex>
  );
};

export default ContributorsTable;
